import * as React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import { styled } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import { PlusCircle, Clock } from "lucide-react";
import { toast } from "react-hot-toast";
import { CreateRequestInput } from "../../../types/explanations";
import {
  useRequestsApi,
  type RequestResponse,
} from "../../../hooks/useRequestsApi";
import RequestForm from "./RequestForm";
import MyRequests from "./MyRequests";
import SlotSelectorModal from "./SlotSelectorModal";
import RequestDetailsModal from "./RequestDetailsModal";

const CustomTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontWeight: theme.typography.fontWeightMedium,
  fontSize: theme.typography.pxToRem(14),
  minHeight: "48px",
  padding: "8px 16px",
  [theme.breakpoints.down("sm")]: {
    fontSize: theme.typography.pxToRem(12),
    padding: "6px 12px",
    minHeight: "40px",
  },
  "&.Mui-selected": {
    backgroundColor: "#E3B505",
    color: "black",
  },
}));

const StudentExplanations: React.FC = () => {
  const [value, setValue] = React.useState(0);
  const [requests, setRequests] = React.useState<RequestResponse[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [showSlotSelector, setShowSlotSelector] = React.useState(false);
  const [showDetailsModal, setShowDetailsModal] = React.useState(false);
  const [selectedRequest, setSelectedRequest] =
    React.useState<RequestResponse | null>(null);
  const [unreadCount, setUnreadCount] = React.useState(0);

  // Utiliser le nouveau hook pour l'API
  const { getMyRequests, createRequest, confirmSlot } = useRequestsApi();

  // Charger les demandes de l'élève
  const fetchMyRequests = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await getMyRequests();

      if (response.status === 200 && Array.isArray(response.body)) {
        setError(null);
        setRequests(response.body);

        // Compter les demandes non lues (statut changé)
        const unread = response.body.filter(
          (req) =>
            req.status === "slots_proposed" || req.status === "validated",
        ).length;
        setUnreadCount(unread);
      } else {
        setError("Erreur lors de la récupération de vos demandes.");
      }
    } catch (error) {
      console.error("Erreur lors du chargement des demandes:", error);
      setError("Une erreur s'est produite lors de la connexion au serveur.");
    } finally {
      setLoading(false);
    }
  }, [getMyRequests]);

  React.useEffect(() => {
    fetchMyRequests();
  }, [fetchMyRequests]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleSubmitRequest = async (formData: CreateRequestInput) => {
    console.log("📝 [StudentExplanations] Soumission de la demande:", formData);

    try {
      const response = await createRequest(formData);
      console.log("📨 [StudentExplanations] Réponse reçue:", response);

      if (response.status >= 200 && response.status < 300) {
        if (
          response.body &&
          typeof response.body === "object" &&
          "message" in response.body &&
          !("_id" in response.body)
        ) {
          console.error(
            "❌ [StudentExplanations] Erreur serveur:",
            response.body.message,
          );
          toast.error(
            (response.body.message as string) ||
              "Erreur lors de l'envoi de la demande",
          );
          return;
        }

        toast.success(
          "Demande envoyée avec succès ! Vous recevrez un email de confirmation.",
        );
        console.log("✅ [StudentExplanations] Demande créée:", response.body);

        await fetchMyRequests();
        setValue(1); // Passer à l'onglet "Mes demandes"
      } else {
        console.error(
          "❌ [StudentExplanations] Status HTTP d'erreur:",
          response.status,
        );
        const errorMessage =
          response.body &&
          typeof response.body === "object" &&
          "message" in response.body
            ? (response.body.message as string)
            : "Erreur lors de l'envoi de la demande";
        toast.error(errorMessage);
      }
    } catch (error: any) {
      console.error("❌ [StudentExplanations] Exception:", error);
      toast.error(error?.message || "Erreur lors de l'envoi de la demande");
    }
  };

  // Ouvrir le sélecteur de créneaux
  const handleSelectSlot = (request: RequestResponse | any) => {
    setSelectedRequest(request);
    setShowSlotSelector(true);
  };

  // Ouvrir le modal de détails
  const handleViewDetails = (request: RequestResponse | any) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  // Confirmer la sélection d'un créneau
  const handleConfirmSlot = async (slotId: string) => {
    if (!selectedRequest) return;

    try {
      const response = await confirmSlot(selectedRequest._id, slotId);
      console.log("📨 [StudentExplanations] Réponse confirmSlot:", response);

      // Vérifier si la réponse est un succès (200-299)
      if (response.status >= 200 && response.status < 300) {
        // Succès
        toast.success(
          "Créneau confirmé avec succès ! Vous recevrez un email de rappel.",
        );

        // Mise à jour optimiste
        setRequests((prevRequests) =>
          prevRequests.map((req) =>
            req._id === selectedRequest._id
              ? { ...req, status: "student_confirmed" }
              : req,
          ),
        );

        // Fermer la modale
        setShowSlotSelector(false);
        setSelectedRequest(null);
      } else {
        const errorMessage =
          response.body &&
          typeof response.body === "object" &&
          "message" in response.body
            ? (response.body.message as string)
            : "Erreur lors de la confirmation du créneau";
        toast.error(errorMessage);
      }
    } catch (error: any) {
      console.error("❌ [StudentExplanations] Exception:", error);
      toast.error(
        error?.message || "Erreur lors de la confirmation du créneau",
      );
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-orange-100 text-orange-800 border border-orange-200";
      case "validated":
        return "bg-green-100 text-green-800 border border-green-200";
      case "slots_proposed":
        return "bg-blue-100 text-blue-800 border border-blue-200";
      case "student_confirmed":
        return "bg-indigo-100 text-indigo-800 border border-indigo-200";
      case "meeting_scheduled":
        return "bg-emerald-100 text-emerald-800 border border-emerald-200";
      case "scheduled":
        return "bg-green-100 text-green-800 border border-green-200";
      case "rejected":
        return "bg-red-100 text-red-800 border border-red-200";
      case "completed":
        return "bg-gray-100 text-gray-800 border border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border border-gray-200";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "pending":
        return "En attente de validation";
      case "validated":
        return "Validée - Prof assigné";
      case "slots_proposed":
        return "Créneaux proposés - À confirmer";
      case "student_confirmed":
        return "Créneau confirmé";
      case "meeting_scheduled":
        return "Réunion programmée";
      case "scheduled":
        return "Séance programmée";
      case "rejected":
        return "Rejetée";
      case "completed":
        return "Terminée";
      default:
        return "Statut inconnu";
    }
  };

  if (loading && requests.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && requests.length === 0) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          maxWidth: "full",
          margin: "auto",
          bgcolor: "background.paper",
          p: { xs: 1, sm: 2 },
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
            Explications d'exercices
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Demandez une explication personnalisée pour vos exercices difficiles
          </Typography>
        </Box>

        {/* Barre d'onglets */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: "100%",
              overflow: "hidden",
              mb: 2,
            }}
          >
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="explications d'exercices"
              textColor="inherit"
              TabIndicatorProps={{ style: { display: "none" } }}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                "& .MuiTabs-scrollButtons": {
                  "&.Mui-disabled": {
                    opacity: 0.3,
                  },
                },
                "& .MuiTabs-scrollButtons.MuiTabs-scrollButtonsDesktop": {
                  display: { xs: "flex", sm: "flex" },
                },
              }}
            >
              <CustomTab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PlusCircle size={18} />
                    <span>Nouvelle demande</span>
                  </Box>
                }
              />
              <CustomTab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Clock size={18} />
                    <span>Mes demandes</span>
                    {unreadCount > 0 && (
                      <Box
                        component="span"
                        sx={{
                          ml: 1,
                          px: 1.5,
                          py: 0.5,
                          borderRadius: "9999px",
                          fontSize: "0.75rem",
                          fontWeight: "medium",
                          bgcolor: "#FEE2E2",
                          color: "#991B1B",
                        }}
                      >
                        {unreadCount}
                      </Box>
                    )}
                  </Box>
                }
              />
            </Tabs>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Contenu des onglets */}
        {value === 0 && (
          <Box className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
            <RequestForm onSubmit={handleSubmitRequest} />
          </Box>
        )}

        {value === 1 && (
          <Box className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
            <MyRequests
              requests={requests}
              loading={loading}
              onSelectSlot={handleSelectSlot}
              onViewDetails={handleViewDetails}
              getStatusBadgeColor={getStatusBadgeColor}
              getStatusText={getStatusText}
            />
          </Box>
        )}
      </Box>

      {/* Modales */}
      <Box
        sx={{ display: "flex", justifyContent: "center", mt: 4, zIndex: 9999 }}
      >
        {showSlotSelector && selectedRequest && (
          <SlotSelectorModal
            request={selectedRequest}
            onConfirm={handleConfirmSlot}
            onClose={() => {
              setShowSlotSelector(false);
              setSelectedRequest(null);
            }}
          />
        )}

        {showDetailsModal && selectedRequest && (
          <RequestDetailsModal
            open={showDetailsModal}
            request={selectedRequest}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedRequest(null);
            }}
            getStatusBadgeColor={getStatusBadgeColor}
            getStatusText={getStatusText}
          />
        )}
      </Box>
    </>
  );
};

export default StudentExplanations;
